import { makeAutoObservable, runInAction } from 'mobx';

import { createPersistStore, PersistStorage } from 'store/persistStore';
import { AccountStatus } from 'components/users/users.enums';

import { PrequalificationApi } from './prequalification.api';
import {
  AssignedTeamMember,
  CompletedPrequalificationStepsDto,
  EntryFormRequestDto,
  PrequalificationInformationDto,
} from './prequalification.dto';
import {
  PrequalificationStepType,
  PrequalificationStep,
  PrequalificationEntrySource,
} from './RegistrationProcess/registrationProcess.enums';
import { PREQUALIFICATION_STEPS_ORDER, REGISTRATION_STEPS } from './RegistrationProcess/registrationProcess.constants';
import { EntryFormResultStatus, ExpiredSessionPageType } from './prequalification.enums';

class PrequalificationStore {
  prequalificationApi: PrequalificationApi;

  intermediaryApplicationId?: string;

  isLoading = false;

  mainContact?: { firstName: string; lastName: string; email: string };

  entryFormStatus?: EntryFormResultStatus;

  assignedTeamMember?: AssignedTeamMember;

  prequalificationInformation?: PrequalificationInformationDto;

  completedSteps?: CompletedPrequalificationStepsDto;

  currentStep?: PrequalificationStep;

  lastOpenedStep?: PrequalificationStep;

  prequalificationEntrySource?: PrequalificationEntrySource;

  expiredSessionPageType?: ExpiredSessionPageType;

  constructor() {
    this.currentStep = PrequalificationStep.BusinessInformation;
    this.lastOpenedStep = PrequalificationStep.BusinessInformation;
    this.prequalificationEntrySource = PrequalificationEntrySource.Registration;
    this.expiredSessionPageType = ExpiredSessionPageType.Login;
    this.prequalificationApi = new PrequalificationApi(this.prequalificationEntrySource);

    makeAutoObservable(this, {}, { autoBind: true });
    createPersistStore({
      store: this,
      storageKey: 'prequalification',
      fieldsToPersist: ['intermediaryApplicationId', 'entryFormStatus', 'mainContact', 'assignedTeamMember'],
      storageType: PersistStorage.Session,
    });
  }

  public async submitEntryForm(entryForm: EntryFormRequestDto) {
    this.isLoading = true;

    try {
      const result = await this.prequalificationApi.submitEntryForm(entryForm);

      runInAction(() => {
        this.intermediaryApplicationId = result.intermediaryApplicationId;
        this.entryFormStatus = result.status;
        this.assignedTeamMember = result.assignedTeamMember;
        this.mainContact = {
          firstName: entryForm.contractorApplication.mainContact.firstName,
          lastName: entryForm.contractorApplication.mainContact.lastName,
          email: entryForm.contractorApplication.mainContact.email,
        };
      });

      return result;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  public async getExpiredSessionPageType(applicationId: string) {
    this.isLoading = true;

    try {
      const { accountStatus } = await this.prequalificationApi.getAccountStatus(applicationId);

      runInAction(() => {
        if (accountStatus === AccountStatus.Active || accountStatus === AccountStatus.Disabled) {
          this.expiredSessionPageType = ExpiredSessionPageType.SendEmail;
        } else {
          this.expiredSessionPageType = ExpiredSessionPageType.Login;
        }
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  public async getRegistrationSessionStartTimestamp(applicationId: string) {
    this.isLoading = true;

    try {
      const result = await this.prequalificationApi.getRegistrationSessionStartTimestamp(applicationId);

      return result;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  public setNextStepByCurrentStep(currentStep: PrequalificationStep) {
    runInAction(() => {
      const incompleteNextStep = REGISTRATION_STEPS.find((step: PrequalificationStep) => {
        return (
          this.completedSteps![step] !== PrequalificationStepType.Complete &&
          PREQUALIFICATION_STEPS_ORDER[currentStep] < PREQUALIFICATION_STEPS_ORDER[step]
        );
      });

      if (!incompleteNextStep) {
        const nextStepIndex = REGISTRATION_STEPS.findIndex((step: PrequalificationStep) => step === currentStep);

        this.setCurrentStep(REGISTRATION_STEPS[nextStepIndex + 1] || PrequalificationStep.Owners);
      } else {
        this.setCurrentStep(incompleteNextStep as PrequalificationStep);
      }
    });
  }

  public setCurrentStep(step: PrequalificationStep) {
    runInAction(() => {
      this.currentStep = step;

      if (PREQUALIFICATION_STEPS_ORDER[this.lastOpenedStep!] < PREQUALIFICATION_STEPS_ORDER[step]) {
        this.lastOpenedStep = step;
      }
    });
  }

  public setPrequalificationEntrySource(source: PrequalificationEntrySource) {
    runInAction(() => {
      this.prequalificationEntrySource = source;
      this.prequalificationApi = new PrequalificationApi(source);
    });
  }

  public async sendInvite(email: string) {
    this.isLoading = true;

    try {
      const result = await this.prequalificationApi.sendInvite({ email: email });

      return result;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  public async getPrequalificationInformation(applicationId: string) {
    this.isLoading = true;

    try {
      const result = await this.prequalificationApi.getPrequalificationInformation(applicationId);

      runInAction(() => {
        this.prequalificationInformation = result;
      });

      return result;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  public async updatePrequalificationInformation(applicationId: string, data: PrequalificationInformationDto) {
    this.isLoading = true;

    try {
      const result = await this.prequalificationApi.updatePrequalificationInformation(applicationId, data);

      await this.getCompletedPrequalificationSteps(applicationId);

      runInAction(() => {
        this.prequalificationInformation = result;
      });

      return result;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  public async getCompletedPrequalificationSteps(applicationId: string) {
    this.isLoading = true;

    try {
      const result = await this.prequalificationApi.getCompletedPrequalificationSteps(applicationId);

      runInAction(() => {
        if (result[PrequalificationStep.Owners] === PrequalificationStepType.Complete) {
          result[PrequalificationStep.Owners] = PrequalificationStepType.Incomplete;
        }

        this.completedSteps = result;

        const currentStep = Object.keys(result).find(
          (step) => result[step as PrequalificationStep] !== PrequalificationStepType.Complete,
        );

        this.currentStep = (currentStep as PrequalificationStep) || PrequalificationStep.Owners;
      });

      return result;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  public async getPrefilledEntryForm(companyHubspotId: string) {
    this.isLoading = true;

    try {
      return await this.prequalificationApi.getPrefilledEntryForm(companyHubspotId);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  public setIntermediaryApplicationId(intermediaryApplicationId: string) {
    runInAction(() => {
      this.intermediaryApplicationId = intermediaryApplicationId;
    });
  }
}

export default PrequalificationStore;
